import axios from "axios";
import doRequest from "services/helpers";
import { getToken } from "services/auth/tokenAuth";
import { currentUserLocalStorage } from "services/storage/general";
import { IS_PRODUCTION } from "config/deploymentMode";

const getCurrentUserCode = () => {
	const currentUser = currentUserLocalStorage.get();
	return currentUser?.id ? `${currentUser.id}` : "";
};

const buildQueryString = (filters = {}) => {
	const params = new URLSearchParams();
	if (typeof filters.campusId === "number") {
		params.append("campusId", filters.campusId);
	}
	if (filters.signatureTypeCode) {
		params.append("signatureTypeCode", filters.signatureTypeCode);
	}
	if (filters.status) {
		params.append("status", filters.status);
	}
	if (filters.validity) {
		params.append("validity", filters.validity);
	}
	if (typeof filters.search === "string" && filters.search.trim()) {
		params.append("search", filters.search.trim());
	}
	if (typeof filters.page === "number" && filters.page > 0) {
		params.append("page", filters.page);
	}
	if (typeof filters.pageSize === "number" && filters.pageSize > 0) {
		params.append("pageSize", filters.pageSize);
	}
	const queryString = params.toString();
	return queryString ? `?${queryString}` : "";
};

const normalizeSignatureFile = signatureFile => ({
	name: signatureFile?.name || "",
	size: Number(signatureFile?.size) || 0,
	base64: signatureFile?.base64 || "",
	contentType: signatureFile?.contentType || "",
	url: signatureFile?.url || "",
	uploadUrl: signatureFile?.uploadUrl || "",
	path: signatureFile?.path || "",
	bucketName: signatureFile?.bucketName || "",
	checksum: signatureFile?.checksum || "",
	urlExpiresAt: signatureFile?.urlExpiresAt || null,
	uploadUrlExpiresAt: signatureFile?.uploadUrlExpiresAt || null,
	rawFile: null,
});

const normalizeEventSignatureItem = item => ({
	id: Number(item?.id) || 0,
	signatureTypeId: Number(item?.signatureTypeId) || 0,
	signatureTypeCode: item?.signatureTypeCode || "",
	displayName: item?.displayName || "",
	utpCode: item?.utpCode || "",
	campusIds: Array.isArray(item?.campusIds)
		? item.campusIds.map(campusId => Number(campusId)).filter(Boolean)
		: [],
	isActive: Boolean(item?.isActive),
	validFrom: item?.validFrom || -1,
	validTo: item?.validTo ?? null,
	isIndefiniteValidity: Boolean(item?.isIndefiniteValidity),
	signatureFile: normalizeSignatureFile(item?.signatureFile),
	updatedAt: item?.updatedAt || 0,
});

const normalizeTypeItem = item => ({
	id: Number(item?.id) || 0,
	code: item?.code || "",
	name: item?.name || "",
	hierarchyWeight: Number(item?.hierarchyWeight) || 0,
	requiresSpeakerData: Boolean(item?.requiresSpeakerData),
});

const getErrorCode = payload => {
	if (!payload) return "";
	if (typeof payload === "string") return payload;
	return (
		payload.ExceptionMessage ||
		payload.exceptionMessage ||
		payload.Message ||
		payload.message ||
		payload.error ||
		""
	);
};

const EVENT_SIGNATURE_ERROR_MESSAGES = {
	EVENT_SIGNATURE_INSTITUTIONAL_CONFLICT:
		"Ya existe una firma institucional activa con el mismo tipo, campus y vigencia.",
	EVENT_SIGNATURE_INSTITUTIONAL_SINGLE_CAMPUS:
		"Las firmas institucionales deben asociarse a un único campus.",
	EVENT_SIGNATURE_CAMPUS_REQUIRED:
		"Selecciona al menos un campus.",
	EVENT_SIGNATURE_FILE_REQUIRED:
		"Debes adjuntar un archivo de firma.",
	EVENT_SIGNATURE_FILE_EXTENSION_INVALID:
		"Solo se permiten archivos PNG para la firma.",
	EVENT_SIGNATURE_FILE_CONTENT_TYPE_INVALID:
		"El archivo de firma debe enviarse como image/png.",
	EVENT_SIGNATURE_FILE_INVALID:
		"No pudimos procesar el archivo de firma. Vuelve a cargarlo.",
	EVENT_SIGNATURE_FILE_UPLOAD_URL_ERROR:
		"No pudimos generar la URL de carga para la firma.",
	EVENT_SIGNATURE_FILE_UPLOAD_URL_INVALID:
		"No recibimos una URL de carga válida para la firma.",
	EVENT_SIGNATURE_FILE_CACHE_INVALIDATION_ERROR:
		"No pudimos preparar el reemplazo del archivo de firma.",
	EVENT_SIGNATURE_FILE_COMPENSATION_REQUIRED:
		"No recibimos la metadata necesaria para compensar el archivo de firma.",
	EVENT_SIGNATURE_FILE_COMPENSATION_INVALID:
		"La metadata del archivo de firma no es válida para compensación.",
	EVENT_SIGNATURE_FILE_COMPENSATION_ERROR:
		"No pudimos revertir el archivo subido en storage.",
	EVENT_SIGNATURE_FILE_PERSISTENCE_CHECK_REQUIRED:
		"No recibimos la metadata necesaria para verificar la persistencia del archivo.",
	EVENT_SIGNATURE_FILE_IN_USE:
		"El archivo ya está siendo utilizado por una firma registrada y no puede eliminarse.",
	EVENT_SIGNATURE_TYPE_INVALID:
		"El tipo de firma seleccionado no es válido para este módulo.",
	EVENT_SIGNATURE_VALID_FROM_REQUIRED:
		"Selecciona la fecha de inicio de vigencia.",
	EVENT_SIGNATURE_VALID_TO_REQUIRED:
		"Selecciona la fecha fin de vigencia o marca vigencia indeterminada.",
	EVENT_SIGNATURE_VALIDITY_INVALID:
		"La fecha fin debe ser mayor o igual a la fecha inicio.",
	EVENT_SIGNATURE_DISPLAY_NAME_REQUIRED:
		"Ingresa el nombre visible de la firma.",
	EVENT_SIGNATURE_REQUEST_REQUIRED:
		"No se pudo procesar la solicitud de la firma.",
	EVENT_SIGNATURE_NOT_FOUND:
		"La firma seleccionada ya no existe o no está disponible.",
};

const getBackendErrorMessage = error => {
	const responseData = error?.response?.data;
	const errorCode = getErrorCode(responseData);
	if (EVENT_SIGNATURE_ERROR_MESSAGES[errorCode]) {
		return EVENT_SIGNATURE_ERROR_MESSAGES[errorCode];
	}
	if (responseData?.message) {
		return responseData.message;
	}
	if (errorCode) {
		return errorCode;
	}
	return "No pudimos completar la operación del administrador de firmas.";
};

const unwrapData = (result, err) => {
	if (err) {
		throw new Error(getBackendErrorMessage(err));
	}
	const { data } = result || {};
	if (data?.state !== 1) {
		throw new Error(
			data?.message ||
				"No pudimos completar la operación del administrador de firmas."
		);
	}
	return data.data;
};

const buildUpsertPayload = payload => ({
	...payload,
	codigoUsuario: getCurrentUserCode(),
	signatureFile: {
		name: payload?.signatureFile?.name || "",
		base64: "",
		contentType: payload?.signatureFile?.contentType || "",
		path: payload?.signatureFile?.path || "",
		bucketName: payload?.signatureFile?.bucketName || "",
		checksum: payload?.signatureFile?.checksum || "",
	},
});

const buildUploadContextPayload = payload => ({
	...payload,
	codigoUsuario: getCurrentUserCode(),
	signatureFile: {
		name: payload?.signatureFile?.name || "",
		contentType: payload?.signatureFile?.contentType || "",
	},
});

const getBufferFromFile = async file => {
	if (!file) throw new Error("No se recibió el archivo de firma.");
	if (typeof file.arrayBuffer !== "function") {
		throw new Error(
			"Tu navegador no soporta la lectura requerida para validar la firma."
		);
	}
	return file.arrayBuffer();
};

const getCryptoApi = () =>
	typeof window !== "undefined" ? window.crypto || window.msCrypto : null;

const getChecksumSupportState = file => {
	const subtleApi = getCryptoApi()?.subtle;
	const supportsArrayBuffer = typeof file?.arrayBuffer === "function";
	const supportsDigest = Boolean(subtleApi?.digest);

	return {
		subtleApi,
		supportsArrayBuffer,
		supportsChecksum: supportsArrayBuffer && supportsDigest,
		isSecureContext:
			typeof window === "undefined" ? true : window.isSecureContext !== false,
	};
};

const resolveChecksumSupportError = ({
	supportsArrayBuffer,
	isSecureContext,
}) => {
	if (!supportsArrayBuffer) {
		return new Error(
			"Tu navegador no soporta la lectura requerida para validar la firma."
		);
	}

	if (!isSecureContext) {
		return new Error(
			"La carga de firmas requiere un contexto seguro (HTTPS o localhost)."
		);
	}

	return new Error(
		"Tu navegador no soporta el cálculo de checksum para firmas."
	);
};

export const computeEventSignatureFileChecksum = async file => {
	const supportState = getChecksumSupportState(file);

	if (!supportState.supportsChecksum) {
		if (!IS_PRODUCTION) {
			return "";
		}
		throw resolveChecksumSupportError(supportState);
	}

	const buffer = await getBufferFromFile(file);
	const hashBuffer = await supportState.subtleApi.digest("SHA-256", buffer);
	return Array.from(new Uint8Array(hashBuffer))
		.map(value => value.toString(16).padStart(2, "0"))
		.join("");
};

export const requestGetEventSignatureAdminTypes = async () => {
	const [result, err] = await doRequest(
		"/Config/EventoFirmas/Tipos",
		"GET",
		null,
		getToken()
	);
	const data = unwrapData(result, err);
	return Array.isArray(data)
		? data
				.map(normalizeTypeItem)
				.sort((left, right) => {
					if (right.hierarchyWeight !== left.hierarchyWeight) {
						return right.hierarchyWeight - left.hierarchyWeight;
					}
					return left.id - right.id;
				})
		: [];
};

export const requestGetEventSignatureAdminList = async (filters = {}) => {
	const [result, err] = await doRequest(
		`/Config/EventoFirmas${buildQueryString(filters)}`,
		"GET",
		null,
		getToken()
	);
	const data = unwrapData(result, err);
	return {
		items: Array.isArray(data?.items)
			? data.items.map(normalizeEventSignatureItem)
			: [],
		page: Number(data?.page) || filters.page || 1,
		pageSize: Number(data?.pageSize) || filters.pageSize || 20,
		total: Number(data?.total) || 0,
	};
};

export const requestGetEventSignatureAdminFileUrl = async id => {
	const [result, err] = await doRequest(
		`/Config/EventoFirmas/${id}/file-url`,
		"GET",
		null,
		getToken()
	);
	const data = unwrapData(result, err);
	return normalizeSignatureFile(data?.signatureFile);
};

export const requestCompensateEventSignatureAdminFile = async signatureFile => {
	const [result, err] = await doRequest(
		"/Config/EventoFirmas/file-compensation",
		"POST",
		{
			codigoUsuario: getCurrentUserCode(),
			signatureFile: {
				name: signatureFile?.name || "",
				base64: "",
				contentType: signatureFile?.contentType || "",
				path: signatureFile?.path || "",
				bucketName: signatureFile?.bucketName || "",
				checksum: signatureFile?.checksum || "",
			},
		},
		getToken()
	);
	const data = unwrapData(result, err);
	return {
		deleted: Boolean(data?.deleted),
		signatureFile: normalizeSignatureFile(data?.signatureFile),
	};
};

export const requestCheckEventSignatureAdminFilePersistence = async (
	signatureFile
) => {
	const [result, err] = await doRequest(
		"/Config/EventoFirmas/file-persistence-check",
		"POST",
		{
			signatureFile: {
				name: signatureFile?.name || "",
				base64: "",
				contentType: signatureFile?.contentType || "",
				path: signatureFile?.path || "",
				bucketName: signatureFile?.bucketName || "",
				checksum: signatureFile?.checksum || "",
			},
		},
		getToken()
	);
	const data = unwrapData(result, err);
	return {
		persisted: Boolean(data?.persisted),
		eventSignatureId:
			typeof data?.eventSignatureId === "number"
				? data.eventSignatureId
				: null,
	};
};

export const requestCreateEventSignatureAdmin = async payload => {
	const [result, err] = await doRequest(
		"/Config/EventoFirmas",
		"POST",
		buildUpsertPayload(payload),
		getToken()
	);
	return normalizeEventSignatureItem(unwrapData(result, err));
};

export const requestCreateEventSignatureAdminUploadContext = async payload => {
	const [result, err] = await doRequest(
		"/Config/EventoFirmas/upload-context",
		"POST",
		buildUploadContextPayload(payload),
		getToken()
	);
	const data = unwrapData(result, err);
	return {
		signatureFile: normalizeSignatureFile(data?.signatureFile),
	};
};

export const requestUpdateEventSignatureAdmin = async (id, payload) => {
	const [result, err] = await doRequest(
		`/Config/EventoFirmas/${id}`,
		"PUT",
		buildUpsertPayload(payload),
		getToken()
	);
	return normalizeEventSignatureItem(unwrapData(result, err));
};

export const requestUpdateEventSignatureAdminUploadContext = async (
	id,
	payload
) => {
	const [result, err] = await doRequest(
		`/Config/EventoFirmas/${id}/upload-context`,
		"PUT",
		buildUploadContextPayload(payload),
		getToken()
	);
	const data = unwrapData(result, err);
	return {
		signatureFile: normalizeSignatureFile(data?.signatureFile),
	};
};

export const requestToggleEventSignatureAdmin = async (id, isActive) => {
	const [result, err] = await doRequest(
		`/Config/EventoFirmas/${id}/${isActive ? "activar" : "desactivar"}`,
		"PATCH",
		{
			codigoUsuario: getCurrentUserCode(),
		},
		getToken()
	);
	return normalizeEventSignatureItem(unwrapData(result, err));
};

export const uploadEventSignatureFile = async ({
	uploadUrl,
	file,
	contentType,
}) => {
	if (!uploadUrl) {
		throw new Error("No recibimos una URL válida para subir la firma.");
	}
	try {
		await axios.put(uploadUrl, file, {
			headers: {
				"Content-Type": contentType || "image/png",
			},
		});
	} catch (error) {
		throw new Error("No pudimos subir el archivo de firma.");
	}
};
